import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { textType } from 'resources/AppTexts';
import { useState } from 'react';
import { Grid, IconButton, Typography } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { AppColors } from 'resources/AppColors';
import { useDispatch, useSelector } from 'react-redux';
import ScheduledFormToDisplay from './ScheduledFormToDisplay';
import DialogMonitoring from 'components/DialogMonitoring';
import { CommonTexts, LabelsLogOut, scheduledForms } from 'locale/es';
import TextMonitoring from 'components/TextMonitoring';
import CustomDialog from 'components/Dialogs/MasterDialog';
import NoDataTableBody from 'components/TableMonitoring/Body/NoDataTableBody';
import Loader from 'components/Loader/Loader';
import AddOrEditScheduledForm from './AddOrEditScheduledForm';
import { useFilterContext } from 'context/FilterContext';
import { useFilter } from 'hooks/useFilter';
import {
  createScheduledFormAction,
  updateScheduledFormAction,
} from 'redux/reducers/scheduledFormsReducer';
import FilterTable from 'components/TableMonitoring/Toolbar/Filter';

const tableHeaders = [
  { name: scheduledForms.FORM_NAME, value: 'slug' },
  { name: scheduledForms.STUDY_NAME, value: 'studiesFormId' },
  { name: scheduledForms.FORM_TITLE, value: 'title' },
  { name: scheduledForms.PACKAGE_LABEL, value: 'businessLabel' },
  { name: scheduledForms.PACKAGE, value: 'businessPackage' },
  { name: scheduledForms.DAYS, value: 'daysOpen' },
  { name: scheduledForms.PRIORITY, value: 'type' },
];

export function getFormSpecificName(form, studyData) {
  return (
    studyData?.forms.find(
      (studyForm) => studyForm.slug === form.slug
    )?.name || '-'
  );
}

export const getStudySpecificName = (form, studyData) => {
  const studyId = studyData?.id;
  
  return studyId?.toString() === form.studiesFormId ? studyData?.title : '-';
};

const ScheduledFormsTable = ({
  entityId,
  scheduledForm,
  actions,
  canCreate,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formId, setFormId] = useState('');

  const { selectedFilter, searchTerm } = useFilterContext();

  const dispatch = useDispatch();

  const { data, loading, error } = scheduledForm;

  const {
    data: studyData,
    loading: studyLoading,
    error: studyError,
  } = useSelector(({ study }) => study);

  const adaptedTableHeaders = tableHeaders.map((header) => header.name);
  const { filteredData } = useFilter(data, adaptedTableHeaders);

  const handleShowDeleteModal = (formToDeleteId) => {
    if (formToDeleteId) {
      setFormId(formToDeleteId);
    } else {
      setFormId('');
    }
    setShowDeleteModal((prevState) => !prevState);
  };

  const handleOnConfirmDeleteForm = () => {
    dispatch(actions.remove(formId));
    setShowDeleteModal(false);
    setFormId('');
  };

  const handleShowEditModal = (form) => {
    setShowEditModal(!showEditModal);
    if (form) {
      setFormId(form.id);
    } else {
      setFormId('');
    }
  };

  const handleShowAddModal = () => {
    setShowAddModal(!showAddModal);
  };

  if (loading) {
    return <Loader message={scheduledForms.LOADING} pt={32} />;
  }

  return (
    <>
      <FilterTable data={data} tableHeaders={tableHeaders} />
      <TableContainer component={Paper}>
        <Table aria-label='simple table'>
          <TableHead>
            <TableRow>
              {adaptedTableHeaders.map((header) => (
                <TableCell align='left' key={header}>
                  <Typography variant='subtitle'>{header}</Typography>
                </TableCell>
              ))}
              <TableCell align='center'>
                {canCreate && (
                  <IconButton onClick={handleShowAddModal} size='large'>
                    <AddCircleIcon sx={{ fill: AppColors.PRIMARY }} />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 &&
              (selectedFilter && !searchTerm ? data : filteredData).map(
                (form) => (
                  <ScheduledFormToDisplay
                    key={form.id}
                    form={form}
                    handleShowEditModal={handleShowEditModal}
                    handleShowDeleteModal={handleShowDeleteModal}
                    canEditDelete={canCreate}
                    formNameValue={getFormSpecificName(form, studyData)}
                    studyName={getStudySpecificName(form, studyData)}
                  />
                )
              )}
          </TableBody>
        </Table>
      </TableContainer>
      {data?.length === 0 && (
        <NoDataTableBody
          entity={entityId}
          noDataMessage={
            canCreate ? scheduledForms.NO_DATA : scheduledForms.NO_DATA_PATIENT
          }
        />
      )}
      {data?.length > 0 && filteredData?.length === 0 && searchTerm && (
        <NoDataTableBody
          entity={entityId}
          noDataMessage={scheduledForms.NO_DATA_FILTERS}
        />
      )}
      <CustomDialog
        title={scheduledForms.CREATED_TITLE}
        show={showAddModal}
        handleShow={handleShowAddModal}
      >
        <AddOrEditScheduledForm
          entityId={entityId}
          setShowModal={setShowAddModal}
          onSubmit={createScheduledFormAction}
          dataForm={data}
        />
      </CustomDialog>
      <CustomDialog
        title={scheduledForms.EDITED_TITLE}
        show={showEditModal}
        handleShow={handleShowEditModal}
      >
        <AddOrEditScheduledForm
          entityId={entityId}
          isEdit={true}
          setShowModal={setShowEditModal}
          onSubmit={updateScheduledFormAction}
          selectedFormId={formId}
          dataForm={data}
        />
      </CustomDialog>
      {showDeleteModal && (
        <DialogMonitoring
          textCancelButton={LabelsLogOut.CANCEL}
          title={scheduledForms.DELETED_TITLE}
          textConfirmButton={CommonTexts.DELETE}
          buttonColor={AppColors.RED}
          show={showDeleteModal}
          handleShow={handleShowDeleteModal}
          handleConfirm={handleOnConfirmDeleteForm}
        >
          <Grid>
            <TextMonitoring
              color={AppColors.PRIMARY}
              type={textType.BODY}
              text={scheduledForms.DELETED_CONFIRM}
            />
          </Grid>
        </DialogMonitoring>
      )}
    </>
  );
};

export default ScheduledFormsTable;

